import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, Grid, Divider } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import PlaceIcon from "@mui/icons-material/Place";
import StorageIcon from "@mui/icons-material/Storage";
import AppsIcon from "@mui/icons-material/Apps";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FlagIcon from "@mui/icons-material/Flag";
import { fetchProgram, deleteProgram } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function ProgramDetailPage() {
  const { businessDomainId, businessUnitId, programId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const basePath = `/strategy/${businessDomainId}/business-units/${businessUnitId}/programs/${programId}`;

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try { setData(await fetchProgram(programId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteProgram(programId);
      notify.success(t("message.delete.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/programs`);
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  if (!data) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("program.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />}
            onClick={() => navigate(`${basePath}/edit`)}>{t("common.edit")}</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}>{t("common.delete")}</Button>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("program.name")} value={data.programName} /></Grid>
          <Grid size={12}><DetailField label={t("program.description")} value={data.programDescription} /></Grid>
        </Grid>
      </DetailSection>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<FlagIcon />}
          onClick={() => navigate(`${basePath}/goals`)}>
          {t("program.goal.list.title")}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="outlined" startIcon={<PeopleIcon />}
          onClick={() => navigate(`${basePath}/customer-categories`)}>
          {t("program.customerCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<ShoppingCartIcon />}
          onClick={() => navigate(`${basePath}/product-categories`)}>
          {t("program.productCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<GroupsIcon />}
          onClick={() => navigate(`${basePath}/member-categories`)}>
          {t("program.memberCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<HandshakeIcon />}
          onClick={() => navigate(`${basePath}/partner-categories`)}>
          {t("program.partnerCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<PlaceIcon />}
          onClick={() => navigate(`${basePath}/place-categories`)}>
          {t("program.placeCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<StorageIcon />}
          onClick={() => navigate(`${basePath}/data-categories`)}>
          {t("program.dataCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AppsIcon />}
          onClick={() => navigate(`${basePath}/application-categories`)}>
          {t("program.applicationCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AccountBalanceIcon />}
          onClick={() => navigate(`${basePath}/financial-asset-categories`)}>
          {t("program.financialAssetCategory.list.title")}
        </Button>
        <Button variant="outlined" startIcon={<AssignmentIcon />}
          onClick={() => navigate(`${basePath}/action-plans`)}>
          {t("program.actionPlan.list.title")}
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/programs`)}>{t("common.back")}</Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
